import React, { useEffect, useMemo, useState } from 'react';
import { Search, Gift, Phone, Mail, MapPin, Plus, Minus, X, Loader, Package, TrendingUp, Download, Upload } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);   // email of the selected customer
  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState({ points: '', reason: '' });
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const { toast } = useToast();

  const downloadCsv = async () => {
    setCsvBusy(true);
    try {
      const res = await api.get('/admin/customers-export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `wrappus-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Exported', description: `Downloaded ${customers.length} customer${customers.length !== 1 ? 's' : ''}.` });
    } catch (err) {
      toast({ title: 'Export failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setCsvBusy(false); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/customers-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const errs = data.errors && data.errors.length ? ` (${data.errors.length} row error${data.errors.length !== 1 ? 's' : ''})` : '';
      toast({ title: 'Import complete', description: `${data.imported} row(s), +${data.pointsAdded} pts added${errs}` });
      if (data.errors?.length) console.warn('Import errors:', data.errors);
      load();
    } catch (err) {
      toast({ title: 'Import failed', description: err?.response?.data?.detail || 'Check your CSV format' });
    } finally {
      setCsvBusy(false);
      e.target.value = '';
    }
  };

  const load = () => api.get('/admin/customers').then((r) => setCustomers(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetail(null);
    api.get(`/admin/customers/${encodeURIComponent(selected)}`).then((r) => setDetail(r.data)).catch(() => setDetail({ error: true }));
  }, [selected]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return customers;
    return customers.filter((c) =>
      (c.email || '').toLowerCase().includes(q) ||
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q)
    );
  }, [customers, search]);

  const submitAdjust = async (e) => {
    e.preventDefault();
    const pts = parseInt(adjust.points, 10);
    if (!pts) return;
    setAdjustLoading(true);
    try {
      const { data } = await api.post('/admin/loyalty/adjust', { email: selected, points: pts, reason: adjust.reason });
      toast({ title: 'Points updated', description: `New balance: ${data.balance} pts` });
      setAdjust({ points: '', reason: '' });
      // Refresh detail + list
      const [d, list] = await Promise.all([
        api.get(`/admin/customers/${encodeURIComponent(selected)}`),
        api.get('/admin/customers'),
      ]);
      setDetail(d.data);
      setCustomers(list.data);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Adjustment failed' });
    } finally { setAdjustLoading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Customers & Rewards</h1>
          <p className="text-gray-500 text-sm mt-1">{customers.length} customer{customers.length !== 1 ? 's' : ''} with paid orders. Click a row to view details and adjust loyalty points.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadCsv} disabled={csvBusy || customers.length === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-[#ED2E2E] text-[#ED2E2E] font-semibold text-sm hover:bg-[#FDF3EC] disabled:opacity-50 transition">
            <Download size={15} /> Export CSV{selected ? '' : ''}
          </button>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ED2E2E] hover:bg-[#C81E1E] text-white font-semibold text-sm cursor-pointer transition">
            <Upload size={15} /> Import CSV
            <input type="file" accept=".csv" onChange={uploadCsv} className="hidden" />
          </label>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or phone..." className="w-full pl-11 pr-4 py-2.5 rounded-full border border-gray-200 focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 focus:outline-none" />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="p-8 text-center text-gray-400 text-sm">Loading customers...</div> :
         filtered.length === 0 ? <div className="p-8 text-center text-gray-400 text-sm">No customers found</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FDF3EC] text-[#0F1C2E]">
                <tr>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Contact</th>
                  <th className="text-left px-5 py-3">Orders</th>
                  <th className="text-left px-5 py-3">Total Spent</th>
                  <th className="text-left px-5 py-3">Loyalty Balance</th>
                  <th className="text-left px-5 py-3">Last Order</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.email} onClick={() => setSelected(c.email)} className="border-t border-gray-50 hover:bg-[#FDF3EC]/40 cursor-pointer">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-[#0F1C2E]">{c.name || '(no name)'}</div>
                      <div className="text-xs text-gray-500">{c.email}</div>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-700">
                      {c.phone && <div className="flex items-center gap-1"><Phone size={12} /> {c.phone}</div>}
                      {c.lastAddress && <div className="flex items-center gap-1 mt-0.5 text-gray-500"><MapPin size={12} /> {c.lastAddress}</div>}
                    </td>
                    <td className="px-5 py-3 font-semibold">{c.orderCount}</td>
                    <td className="px-5 py-3 font-semibold text-[#ED2E2E]">${c.totalSpent.toFixed(2)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Gift size={14} className="text-[#ED2E2E]" />
                        <div>
                          <div className="font-bold text-[#0F1C2E]">{c.pointsBalance} pts</div>
                          <div className="text-xs text-gray-400">{c.pointsLifetime} lifetime</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500">{new Date(c.lastOrderAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="font-heading font-bold text-xl text-[#0F1C2E]">Customer Detail</h2>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
            </div>
            {!detail ? <div className="p-10 text-center text-gray-400 flex items-center justify-center gap-2"><Loader size={16} className="animate-spin" /> Loading...</div> :
             detail.error ? <div className="p-10 text-center text-gray-400">Could not load customer details.</div> : (
              <div className="p-6 space-y-6">
                <div>
                  <div className="text-sm text-gray-500">{detail.email}</div>
                  <div className="text-2xl font-heading font-bold text-[#0F1C2E]">{detail.orders[0]?.customerName || '(no name)'}</div>
                  {detail.orders[0]?.customerPhone && <div className="text-sm text-gray-700 flex items-center gap-1 mt-1"><Phone size={13} /> {detail.orders[0].customerPhone}</div>}
                </div>

                {/* Loyalty summary + adjust */}
                <div className="bg-gradient-to-br from-[#ED2E2E] to-[#C81E1E] text-white rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><Gift size={20} /><span className="font-heading font-bold text-lg">Loyalty Points</span></div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{detail.loyalty.balance}</div>
                      <div className="text-xs opacity-80">available now</div>
                    </div>
                  </div>
                  <div className="text-xs opacity-90 mb-4">
                    Lifetime earned: {detail.loyalty.lifetime} · Expiring in 30 days: {detail.loyalty.expiringSoon}
                  </div>
                  <form onSubmit={submitAdjust} className="bg-white/10 rounded-xl p-3 flex flex-col sm:flex-row gap-2">
                    <input type="number" required value={adjust.points} onChange={(e) => setAdjust({ ...adjust, points: e.target.value })} placeholder="+ or – points (e.g. 50 or -25)" className="flex-1 rounded-lg px-3 py-2 bg-white text-[#0F1C2E] focus:outline-none text-sm" />
                    <input value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} placeholder="Reason (optional)" className="flex-1 rounded-lg px-3 py-2 bg-white text-[#0F1C2E] focus:outline-none text-sm" />
                    <button type="submit" disabled={adjustLoading || !adjust.points} className="px-4 py-2 rounded-lg bg-white text-[#ED2E2E] font-semibold text-sm hover:bg-white/90 disabled:opacity-60 flex items-center gap-1">
                      {adjust.points && parseInt(adjust.points, 10) < 0 ? <Minus size={14} /> : <Plus size={14} />} Apply
                    </button>
                  </form>
                </div>

                {/* Points ledger */}
                <div>
                  <div className="flex items-center gap-2 mb-3"><TrendingUp size={18} className="text-[#ED2E2E]" /><h3 className="font-heading font-semibold text-[#0F1C2E]">Points History</h3></div>
                  <div className="bg-[#FDF3EC] rounded-2xl divide-y divide-white/60 max-h-64 overflow-y-auto">
                    {detail.ledger.length === 0 ? <div className="p-4 text-sm text-gray-500 text-center">No points activity yet</div> :
                     detail.ledger.map((e) => (
                       <div key={e.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                         <div>
                           <div className="font-semibold text-[#0F1C2E]">
                             {e.type === 'earn' ? '+' : '−'}{e.points} pts {e.expired && <span className="text-red-600 text-xs">(EXPIRED)</span>}
                           </div>
                           <div className="text-xs text-gray-500">
                             {new Date(e.createdAt).toLocaleString([], { month: 'short', day: 'numeric' })}
                             {e.orderId?.startsWith('ADMIN_ADJUST') && <span className="text-orange-600"> · Admin adjustment</span>}
                             {e.orderId && !e.orderId.startsWith('ADMIN_ADJUST') && <span> · Order #{e.orderId.slice(0, 8)}</span>}
                           </div>
                         </div>
                         {e.type === 'earn' && !e.expired && (
                           <div className="text-xs text-gray-500">
                             {e.consumedPoints > 0 && `${e.consumedPoints} used · `}
                             expires {e.expiresAt ? new Date(e.expiresAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' }) : ''}
                           </div>
                         )}
                       </div>
                     ))
                    }
                  </div>
                </div>

                {/* Orders */}
                <div>
                  <div className="flex items-center gap-2 mb-3"><Package size={18} className="text-[#ED2E2E]" /><h3 className="font-heading font-semibold text-[#0F1C2E]">Order History ({detail.orders.length})</h3></div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {detail.orders.map((o) => (
                      <div key={o.id} className="bg-white border border-gray-100 rounded-xl p-3 text-sm flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-[#0F1C2E]">Order #{o.id.slice(0, 8)} · <span className="text-gray-500 font-normal">{o.type === 'delivery' ? 'Delivery' : 'Pickup'}</span></div>
                          <div className="text-xs text-gray-500">{new Date(o.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#ED2E2E]">${o.total.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{o.status === 'paid' ? (o.kitchenStatus || 'queued') : o.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
